// ============================================================================
//  metrics.js — pure derivations over the {people, goals, marks} state shape.
//
//  No React, no Supabase, no side effects. Everything here takes the denorm-
//  alized `st` object that loadState() returns and answers a question about it,
//  so any screen can compute the same numbers the same way.
// ============================================================================

/** Current month as the "YYYY-MM" key mile_markers is keyed on. */
export function monthKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "2026-07" -> "July 2026" */
export function monthLabel(k) {
  const [y, m] = k.split("-");
  return new Date(+y, +m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Every goal owned by a man, or [] if he has none visible to us. */
export function personGoals(st, personId) {
  return st.goals[personId] || [];
}

/** The most recent Mile Marker logged against a goal, or null. */
export function goalLatest(st, goalId) {
  const a = (st.marks[goalId] || []).slice().sort((x, y) => x.month.localeCompare(y.month));
  return a[a.length - 1] || null;
}

/** Mean completion across one F for one man; null when he has no goals there. */
export function fProgress(st, personId, f) {
  const gs = personGoals(st, personId).filter((g) => g.f === f);
  if (!gs.length) return null;
  return Math.round(gs.map((g) => goalLatest(st, g.id)?.pct ?? 0).reduce((a, b) => a + b, 0) / gs.length);
}

/** Mean completion across all five F's for one man. */
export function overall(st, personId) {
  const gs = personGoals(st, personId);
  if (!gs.length) return 0;
  return Math.round(gs.map((g) => goalLatest(st, g.id)?.pct ?? 0).reduce((a, b) => a + b, 0) / gs.length);
}

/**
 * The worst RAG status across a man's goals — what the roster card shows, so
 * one red goal is never hidden behind four green ones. Null if nothing logged.
 */
export function worstRag(st, personId) {
  const order = { green: 0, amber: 1, red: 2 };
  let w = null;
  personGoals(st, personId).forEach((g) => {
    const r = goalLatest(st, g.id)?.rag;
    if (r && (w === null || order[r] > order[w])) w = r;
  });
  return w;
}

/** Short client-side id for optimistic rows, replaced by the DB uuid on save. */
export const uid = () => Math.random().toString(36).slice(2, 9);
